import { IAppliedOffer, ICertificateJson, IOrderProductJson, ISavingDetails } from './Order';
import { ICustomerAddress, IPaymentProfile } from './Customer';
import { ILocalStoreJson } from "./LocalStore";
import { IProductPriceViewModel } from './Product';

export interface IEnoughForOrder {
	subscription: number;
	isEnoughForOrder: boolean;
}
export interface IShowingSnoozeForm {
	subscription: number;
	show: boolean;
}
export interface IProductSummaryImage {
	fullWebp: string,
	full: string,
	quantity: number,
	alt: string
}

export type ShippingMethod = 'STD' | 'PRI' | null;

export enum SnoozeType {
	ManualSnooze = 1, //User selected snooze option
	Timeout = 2, //No response to subscription reminder email
	Cancellation = 3, //Order placed but later cancelled
	SkipOrder = 4, //User selected to snooze entire order
}

/*============================================================
 == Checkout Session
/============================================================*/

export interface ICheckoutSessionResource {
	userInfo: IUserInfoResource;
	orderInfo: IOrderInfoResource;
	nextStep: CheckoutFormStep;
	fulfillmentInfo: IFulfillmentInfoResource;
	orderSubscriptions: IOrderSubscriptions[] | null;
	canShowPaymentStep: boolean;
	showPaymentFormAlertMessage: boolean;
	paymentFormAlertMessageHtml: string;
	canShowFulfillmentStep: boolean;
}

export interface IOrderSubscriptions {
	id: number;
	subscriptionID: number,
	blainNumber: string;
	mfr: string;
	frequency: number;
	isSnoozed: boolean;
	snoozeDuration?: number;
	snoozeReason?: SnoozeType;
	quantity: number; //quantity to order
	subscribedQuantity: number; //quantity of original subscription
	productName: string;
	productBrand: string;
	imageUrl: string;
	imageUrlWebP: string;
	price: IProductPriceViewModel;
	appliedProductDiscounts:ICheckoutOrderDiscountResource[];
}

export interface ISnoozeRequest {
	snoozeReason: number;
	subscriptionID: number;
	snoozeLength: number
}

export interface IUnSnoozeRequest {
	subscriptionID: number;
}

export interface ISubscriptionProductRequest {
	itemID: number;
	itemQty: number;
	blainNumber: string;
}

export interface ICheckoutThankYouResource {
	isFarmsSnoozedOrder: boolean;
	isFarmsSkippedOrder: boolean;
	accountExists: boolean;
	orderDate: string;
	isRewardsMember: boolean;
	orderInfo: IThankYouOrderInfo;
	contactInfo: IThankYouContactInfo;
	fulfillmentInfo: IThankYouFulfillmentInfo;
	orderSubscriptions: IThankYouOrderSubscription[];
}

export interface IThankYouAttributeValue {
	description: string;
	value: string;
}
export interface IThankYouOrderSubscription {
	subscriptionID: number,
	blainNumber: string;
	mfr: string;
	frequency: number;
	isFirstTimeSubscription: boolean,
	isSnoozed: boolean;
	snoozeDuration?: number;
	quantity: number;
	productName: string;
	productBrand: string;
	imageUrl: string;
	imageUrlWebP: string;
	attributes: IThankYouAttributeValue[];
	snoozeReason: SnoozeType;
}

export interface IThankYouOrderInfo {
	orderID: number;
	orderSummary: ICheckoutOrderSummaryResource;
	paymentMethods: IThankYouPayment[];
	products: IOrderProductJson[];
	giftCards: IOrderGiftCardResource[];
}

export interface IThankYouContactInfo {
	emailAddress: string;
	phone: string;
	firstName: string;
}

export interface IThankYouFulfillmentInfo {
	hasShipItems: boolean;
	hasDeliveryItems: boolean;
	hasPickupItems: boolean;
	hasGiftCardsToShip: boolean;
	productsEstimatedArrivalString: string;
	giftCardEstimatedArrivalString: string;
	fullPriceParcelRate: number;
	ltlRate: number;
}

export interface IThankYouPayment {
	amount: number;
	amountString: string;
	cardType: CheckoutPaymentType;
	description: string;
	paymentMethodID: number;
	billingAddress: ICheckoutPaymentBillingAddressResource;
	isRewardsGiftCard: boolean;
}

export interface IUserInfoResource {
	customerID: number;
	isGuest: boolean;
	isSessionExpired: boolean;
	isRewardsMember: boolean;
	hasRewardsCreditCard: boolean;
	rewardsGiftCards: IVirtualGiftCardResource[];
	billingAddress: ICheckoutAddress;
	contactInfo: IContactInfo;
	savedAddresses: ICustomerAddress[];
	availableTaxExemptProfile: ICertificateJson;
	savedPaymentProfiles: IPaymentProfile[];
}

export interface IOrderInfoResource {
	orderID: number;
	isGiftOrder: boolean;
	submitted: boolean;
	expectedPonumbers: number;
	rewardsNum: string;
	personalMessage: string;
	promoCode: string;
	checkoutPayments: ICheckoutPayment[];
	orderSummary: ICheckoutOrderSummaryResource;
	products: IOrderProductJson[];
	giftCards: IOrderGiftCardResource[];
	restrictedItems: IRestrictedItemInfo;
	isCustomOrder: boolean;
	orderType: OrderType;
	isReplacementOrder: boolean;
	hasSpecialOrderItem: boolean;
	isEmployeeOrder: boolean;
	replaceAuthUser: string;
	isSubscriptionOrder: boolean;
}

/**
 * These strings map to the stringly-typed server side OrderPaymentHelper.OrderType values
 */
export type OrderType = "CPP" | "CSO" | "CWS" | "POS" | "PWS" | "RGC"

export interface IStepInfoResource {
	nextStep: number;
	showAddressVerificationModal: boolean;
	showESEModal: boolean;
	cardForVerification: number;
	addressVerificationWarnings: string[];
}

export interface IFulfillmentInfoResource {
	shipAddress: ICheckoutAddress;
	pickupInfo: IPickupInfo;
	shippingOptionsResult: IShippingInfoResource;
	localStore: ILocalStoreJson;
	hasDeliveryItem: boolean;
	hasPickupItems: boolean;
	hasShipItems: boolean;
	hasDeliveryItemsOnly: boolean;
	hasPickupItemsOnly: boolean;
	hasShippingItemsOnly: boolean;
	hasShipSelfGiftCards: boolean;
	hasGiftCardsToShip: boolean;
	localDeliveryData: ILocalDeliveryData;
	localDeliveryErrors: string;
	shippingCalculationError: string;
	editPhoneOnly: boolean;
	contactInfoComplete: boolean;
}

export interface ICheckoutOrderSummaryResource {
	subTotal: number;
	shipping: number;
	localDeliveryLabel: string;
	estimatedLocalDeliveryDateString: string | null;
	governmentFeeLabel: string;
	governmentFeeString: string;
	hasTaxExemptProfile: boolean;
	tax: number;
	taxableShipping: number;
	taxableShippingString: string;
	taxExemptShipping: number;
	taxExemptShippingString: string;
	taxableLocalDelivery: number;
	taxableLocalDeliveryString: string;
	taxExemptLocalDelivery: number;
	taxExemptLocalDeliveryString: string;
	grandTotal: number;
	remainingBalance: number;
	totalAfterGiftCards: number;
	savings: number;
	savingsString: string;
	totalSavings: number;
	totalSavingsString: string;
	subTotalString: string;
	shippingString: string;
	localDeliveryString: string;
	taxString: string;
	grandTotalString: string;
	remainingBalanceString: string;
	totalAfterGiftCardsString: string;
	taxProfileId: number;
	earnableRewardsPoints: number;
	earnableRewardsPointsString: string;
	savingDetails: ISavingDetails[];
	bouncebackOffers: IAppliedOffer[];
	appliedDiscounts: ISavingDetails[];
	appliedShippingDiscounts: ICheckoutOrderDiscountResource[];
	appliedDeliveryDiscounts: ICheckoutOrderDiscountResource[];
	appliedProductDiscounts: IAppliedProductDiscounts;
	hideTotal: boolean;
	hideSavings: boolean;
	checkoutCTA: string;
	allItemsShipPromoExclude: boolean;
	currentRewardsShippingOffer: string;
}

export type IAppliedProductDiscounts = { // Using an object type instead of a Record here for the documentation benefit of being able to give the key type a name
	[id: string]: ICheckoutOrderDiscountResource[];
}

export interface IPickupInfo {
	firstName: string;
	lastName: string;
	phone: string;
	driveThru: boolean;
	allowSMS: boolean;
	defaultMode: string;
	mode: string;
	availableModes: string[];
	message: string;
}

export interface IContactInfo extends IContactInfoFields {
	id: number;
}

export interface IDeliveryInfoFields extends ICheckoutAddressFields {
	instructions: string;
}

export interface IDeliveryInfo extends IDeliveryInfoFields {}

export interface IShippingInfo extends ICheckoutAddressFields {
	shippingMethod: ShippingMethod;
}

export interface ICheckoutPaymentBillingAddressResource {
	firstName: string;
	lastName: string;
	company: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	zip: string;
	country: string;
}

export interface ILocalDeliveryData {
	orderId: number;
	deliveryNotes: string;
}

export interface IOrderGiftCardResource {
	id: number;
	orderID: number;
	isPromoCard: boolean;
	promoCode: string;
	offerTitle: string;
	qty: number;
	amount: string;
	cardImage: string;
	cardThumb: string;
	description: string;
	deliveryHeader: string;
	envelopeImage: string;
	envelopeThumb: string;
	recipientName: string;
	recipientDescription: string;
	deliveryMethod: string;
	recipientEmail: string;
	lineTotal: string;
	message: string;
}

export interface IShippingInfoResource {
	estimatedArrival: string;
	requiresCustomQuote: boolean;
	errorMessage: string;
	selectedShipMethod: ShippingMethod;
	estimatedArrivalLTL: string;
	effectiveParcelRate: number;
	ltlRate: number;
	ltlRateString: string;
	productsEstimatedArrivalString: string;
	giftCardEstimatedArrivalString: string;
	options: IShippingInfoOptionResource[];
}

export interface IShippingInfoOptionResource {
	estimatedArrival: string;
	rate: string;
	name: string;
	value: string;
}

//Used for aync calls which do not require a full session response
export interface ICheckoutJsonResult {
	Success: boolean;
	ShowExemptModal: boolean;
	ShowRequireCustomQuote: boolean;
	ValidShipDestination: boolean;
}

/*============================================================
 == Payments / Cybersource
/============================================================*/

export interface ICheckoutPayment {
	amount: number;
	amountString: string;
	cardType: CheckoutPaymentType;
	description: string;
	profileID: string;
	paymentMethodID: number;
	expired: boolean;
	maskedCreditCard: string;
	isRewardsGiftCard: boolean;
	giftCardNum: string;
	billingAddress: ICheckoutPaymentBillingAddressResource;
}


export type CheckoutPaymentType =
	| "Visa"
	| "Mastercard"
	| "Discover"
	| "AmericanExpress"
	| "GiftCard"
	| "PayPal"
	| "BlainsCard"
	| "GooglePay";

export type CheckoutPaymentMethods = 'savedCard' | 'newCard' | 'paypal' | 'googlepay';

export interface IGiftCard {
	giftCardNum: string;
	giftCardPin: string;
	giftCardID?: number;
}

export interface ISavedCard {
	profileID: string;
	maxAmount?: number;
	multipleCardPayment: boolean;
}

export interface IGiftOption {
	hasGift: boolean;
	giftMessage: string;
}

export interface ICheckoutStepResponse {
	isSuccess: boolean;
	validationResult: ICheckoutValidationResult;
	errorMessage: string;
	session: ICheckoutSessionResource;
	redirectUrl?: string;
}

export interface IAddSavedCardResponse extends ICheckoutStepResponse {
	isExpired: boolean;
}

export interface ICheckoutOrderDiscountResource {
	id: number;
	orderID: number;
	refCode: string;
	description: string;
	amount: number;
	amountString: string;
	// DisplayType: DiscountDisplayType;
	posLineNum: number;
	offerID: number | null;
	rewardsOfferID: string | null;
	discountType: OrderDiscountType;
	benefitTypeID: PromoBenefitType;
	// TriggerTypeID: PromoTriggerType;
}

export enum OrderDiscountType {
	General,
	Rewards,
	FreeItem,
	GiftCard,
	Shipping,
	Delivery
}

export enum PromoBenefitType {
	GenericPromoMessage = 0,
	FreeOrSpecificPrice = 1,
	PercentOffItem = 2,
	AmountOffItem = 3,
	PercentOffOrder = 4,
	AmountOffOrder = 5,
	ShippingPromo = 6,
	FreeGiftCard = 7,
	AmountOffGiftCard = 8,
	PercentOffGiftCard = 9,
	RewardsPoints = 10,
	MailInRebate = 11,
	IssueOtherOffer = 12,
	DeliveryPromo = 13,
	PosFreeGift = 98,
	LegacyBogoDiscount = 99
}

export interface IRestrictedItemInfo {
	restrictedProductBlainNumbers: string[];
	canRemoveAndContinue: boolean;
	restrictions: IRestriction[];
}

export interface IRestriction {
	type: RestrictionType;
	reason: string | null;
}

export enum RestrictionType {
	Generic,
	Warranty,
	Reseller
}

export interface IResctrictedItem {
	text: string;
	imageUrl: string;
	imageUrlWebP?: string;
	link?: string;
	qty?: number;
}

export enum CheckoutFormStep {
	Subscription = 0,
	Fulfillment = 1,
	Payment = 2,
	Review = 3,
	ThankYou = 4,
	PayPalConfirmation = 5,
	RedirectToBlainPay = 6
}

export type ApplyPromoCodeResponse =
	| { success: true;  session: ICheckoutSessionResource }
	| { success: false; session: ICheckoutSessionResource; message: string }

export type CreditCardName = "visa" | "mastercard" | "amex" | "discover" | null;
export type CyberSourceCardType = "001" | "002" | "003" | "004" | null;

export interface CreditCardType {
	name: CreditCardName;
	pattern?: RegExp;
	validLength: number;
	csNumber: CyberSourceCardType;
}

export interface CyberSourcePostForm {
	fields: SecureAcceptanceRequestField[];
	signatureContent: string;
	formID: string;
	formAction: string;
	isIframe: boolean;
}

export interface SecureAcceptanceRequestField {
	name: string;
	sourceID: string;
	value: string;
}

export interface ICreditCard {
	valid: boolean;
	error: string;
	savedCard: boolean;
	profileID: string;
	details: CreditCardDetails;
	amount?: number;
}

export interface CreditCardDetails {
	ccExpDate: string;
	ccSave: boolean;
	sameAsShipping: boolean;
	billingAddress: ICheckoutAddress
	csNumber: CyberSourceCardType;
}

export interface IVirtualGiftCardResource {
	id: number;
	redactedCardNum: string;
	balance: number | null;
}

export interface ISubmitOrderRequest {
	hasGift: boolean;
	giftMessage: string | null;
	orderMetadata: OrderMetadata;
	localDeliveryInstructions: string | null;
}

export interface IFulfillmentSubmitRequest {
	address: ICheckoutAddress | null;
	pickupInfo: IPickupInfoFields | null;
	shippingMethod: ShippingMethod;
	deliveryInstructions: string | null;
}

/*============================================================
 == Form Fields
/============================================================*/

export interface IPickupInfoFields {
	firstName: string;
	lastName: string;
}

export interface ICheckoutAddressFields {
	firstName: string;
	lastName: string;
	company: string;
	phone: string;
	address1: string;
	address2?: string;
	city: string;
	state: string;
	zip: string;
	saveAddress?: boolean;
	setAsDefault?: boolean;
}

export interface ICheckoutAddress extends ICheckoutAddressFields {
	id?: number | null;
}

export interface IContactInfoFields {
	emailAddress: string;
	phone: string;
	textPromoSignup: boolean;
	textOrderStatus: boolean;
}

/*============================================================
 == Validation
/============================================================*/

export interface OrderMetadata {
	orderId: number,
	orderTotal: number,
}

export type CheckoutFieldErrorGroup<T> = Record<keyof T, string | null> & {
	section: string | null;
}

export interface ICheckoutFieldErrors {
	address: CheckoutFieldErrorGroup<ICheckoutAddressFields>;
	shippingMethod: ShippingMethod;
	deliveryInstructions: string | null;
	contactInfo: CheckoutFieldErrorGroup<IContactInfoFields>;
	pickupInfo: CheckoutFieldErrorGroup<IPickupInfoFields>;
	giftOptions: string | null;
}

export interface CardVerificationResponse {
	redirectToCart?: boolean;
	cardToVerify?: number;
	orderMetadataMismatch?: boolean;
}

export interface ICheckoutValidationResult {
	addressVerificationErrors?: string[];
	suggestedAddress: ICheckoutAddress;
	fieldErrors: ICheckoutFieldErrors;
	hasFieldErrors: boolean;
	invalidSession: boolean;
	globalError: string | null;
	showESEModal: boolean;
	showAddressVerificationModal: boolean;
	checkoutFormStep?: CheckoutFormStep;
	errorType: CheckoutOrderValidationErrorType;
	restrictedItemInfo?: IRestrictedItemInfo;
}

export enum CheckoutOrderValidationErrorType {
	None = 0,
	CheckoutSessionExpired,
	HasFieldErrors,
	CardVerificationRequired,
	LocalDeliveryError,
	HasRestrictedProducts,
	InvalidShipAddress,
	InvalidDeliveryAddress,
	AddressVerificationNeeded,
	MissingPaymentMethod
}